import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';


declare const google;

@Injectable({
  providedIn: 'root'
})
export class GoogleDriveService {
  private accessToken: string | null = null;
  private tokenClient;

  public constructor() {
    if (google && google.accounts && google.accounts.oauth2) {
      this.initTokenClient();
    }
  }

  private initTokenClient(): void {
    this.tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: environment.clientIdGoogle,
      scope: environment.scopesGoogle,
      callback: (response) => {
        if (response.error) {
          console.error('Error obteniendo el token:', response.error);
          return;
        }
        this.accessToken = response.access_token;
      }
    });
  }

  public async signIn(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.tokenClient.requestAccessToken();
      this.tokenClient.callback = (response): void => {
        if (response.error) {
          reject(response.error);
        } else {
          this.accessToken = response.access_token;
          resolve();
        }
      };
    });
  }

  private async waitForPickerApiLoaded(timeout: number = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const checkLoaded = () => {
        if (google?.picker) {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          reject('Tiempo de espera agotado para la API de Picker.');
        } else {
          setTimeout(checkLoaded, 100);
        }
      };
      checkLoaded();
    });
  }

  public pickFolder(): Promise<string | null> {
    return new Promise((resolve, reject) => {

      const docsView = new google.picker.DocsView()
          .setIncludeFolders(true)
          .setMimeTypes('application/vnd.google-apps.folder')
          .setSelectFolderEnabled(true);

      try {
        this.waitForPickerApiLoaded().then(() => {
          const picker = new google.picker.PickerBuilder()
            .setOAuthToken(this.accessToken)
            .addView(docsView)
            .setCallback((data) => {
              if (data.action === google.picker.Action.PICKED) {
                const folderId = data.docs[0].id;
                resolve(folderId);
              } else if (data.action === google.picker.Action.CANCEL) {
                resolve(null);
              }
            })
            .build();

          picker.setVisible(true);
        }).catch((error) => {
          console.error('Error al cargar la API de Picker:', error);
          reject('No se pudo cargar la API de Picker.');
        });
      } catch (error) {
        console.error('Error al configurar el Picker:', error);
        reject('Error al configurar el Picker.');
      }
    });
  }

  public async pickFile(): Promise<string | null> {
    await this.waitForPickerApiLoaded();

    return new Promise((resolve) => {
      const docsView = new google.picker.DocsView(google.picker.ViewId.DOCUMENTS)
        .setIncludeFolders(true)

      const picker = new google.picker.PickerBuilder()
        .setOAuthToken(this.accessToken)
        .addView(docsView)
        .addView(google.picker.ViewId.FOLDERS)
        .setCallback((data) => {
          if (!data || !data.action) {
            return;
          }
          if (data.action === google.picker.Action.PICKED) {
            const file = data[google.picker.Response.DOCUMENTS][0];
            resolve(file[google.picker.Document.URL]);
          } else if (data.action === google.picker.Action.CANCEL) {
            resolve(null);
          }
        })
        .build();

      picker.setVisible(true);
    });
  }

  public async downloadSelectedFile(): Promise<void> {
    await this.signIn();
    const fileUrl = await this.pickFile();
    if (!fileUrl) {
      return;
    }

    const fileIdMatch = fileUrl.match(/\/d\/(.*?)(\/|$)/);
    if (!fileIdMatch) throw new Error('No se pudo extraer el ID del archivo.');

    const fileId = fileIdMatch[1];

    const metadataResponse = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?fields=name,mimeType`,
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      }
    );
    if (!metadataResponse.ok)
      throw new Error(`Error al obtener metadata: ${metadataResponse.statusText}`);
    const { name, mimeType } = await metadataResponse.json();

    let downloadUrl = '';
    if (mimeType === 'application/vnd.google-apps.document') {
      downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/pdf`;
    } else if (mimeType === 'application/vnd.google-apps.spreadsheet') {
      downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`;
    } else if (mimeType === 'application/vnd.google-apps.presentation') {
      downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/pdf`;
    } else {
      downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
    }

    const downloadResponse = await fetch(downloadUrl, {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
    });
    if (!downloadResponse.ok)
      throw new Error(`Error al descargar el archivo: ${downloadResponse.statusText}`);

    const blob = await downloadResponse.blob();

    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.download = name || 'archivo_descargado';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(downloadLink.href);
  }

  public async uploadFile(file: Blob, fileName: string, folderId?: string): Promise<any> {
    if (!this.accessToken) {
      await this.signIn();
    }

    try {
      const metadata = {
        name: fileName,
        mimeType: file.type,
        parents: [folderId],
      };

      if (folderId) {
        metadata.parents = [folderId];
      }

      const formData = new FormData();
      formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      formData.append('file', file);

      const response = await fetch(
        'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id',
        {
          method: 'POST',
          headers: new Headers({ 'Authorization': `Bearer ${this.accessToken}` }),
          body: formData
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }
}
